import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileTextIcon,
  EnvelopeClosedIcon,
} from "@radix-ui/react-icons";
import { CgImport } from "react-icons/cg";
import { GoChecklist } from "react-icons/go";
import {
  ApiError,
  Auth0AccountRole,
  MessageDirection,
  MeetingLimitedResponse,
  MeetingsService,
  MeetingStatus,
  ApprovalStatus,
  OrganizationResponseForAdmins,
  TasksService,
  TaskStatus,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import CalendarWrapper from "components/Dashboard/Calendar";
import { ErrorBlock } from "components/StatusBlock";
import moment from "moment";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { APIResponse, PageStatus } from "types";
import "./index.css";
import TutoringHours from "../TutoringHours";
import { Dialog, DialogTrigger } from "components/Dialog";
import ExportDialog from "../ExportDialog";
import ImportMeetingsDialog from "../ImportMeetingsDialog";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { DateRangeSelector } from "./Components/dateRangeSelector";
import { MeetingFilters } from "../TutoringHours/Components/meetingFilters";
import OrgCommunication from "../OrgCommunication";
import {
  ChannelFilter,
  CommunicationFilters,
} from "../OrgCommunication/CommunicationFilters";
import ManageTasksDialog from "../ManageTasksDialog";
import { TASKS_CHANGED } from "../ManageTasksDialog/tasksChanged";
import { conversationCount, useUnread } from "util/UnreadContext";
import { useMeetingsChanged } from "util/meetingsChanged";
import CountBadge from "components/CountBadge";

type ViewType = "calendar" | "tutoring_hours" | "communication" | "tasks";

const View = {
  CALENDAR: "calendar" as ViewType,
  TUTORING_HOURS: "tutoring_hours" as ViewType,
  COMMUNICATION: "communication" as ViewType,
  TASKS: "tasks" as ViewType,
};

/**
 * How many of the organization's tasks are still to do, for the Tasks icon.
 * Reloads whenever tasks change (the tadpole:tasks-changed event).
 */
function useOpenTaskCount(orgId: number | undefined, enabled: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled || !orgId) return;
    let latest = 0;
    const load = () => {
      const request = ++latest;
      TasksService.getOrgPendingTaskCount({ orgId })
        .then((n) => {
          if (request === latest) setCount(n);
        })
        .catch(() => {});
    };
    load();
    window.addEventListener(TASKS_CHANGED, load);
    return () => {
      latest = -1;
      window.removeEventListener(TASKS_CHANGED, load);
    };
  }, [orgId, enabled]);

  return count;
}

type CalendarViewType = "week" | "day";

/**
 * How many weeks before and after the shown week the calendar loads in one
 * request, so moving between nearby weeks or days is instant. Moving past
 * that edge loads the same stretch around the new week.
 */
export const CALENDAR_PRELOAD_WEEKS = 4;

/** How long the dates stay unchanged before the Communication view loads them. */
const COMMUNICATION_DATE_WAIT_MS = 600;

const CalendarView = {
  WEEK: "week" as CalendarViewType,
  DAY: "day" as CalendarViewType,
};

function UpcomingMeetings({
  children,
  studentFilters,
  tutorFilters,
  subjectFilters,
  locationFilters,
  locationAddressFilters,
  locationCityFilters,
  locationStateFilters,
  addedStart,
  organization,
  onMeetingsChange,
  refreshKey,
}: {
  children?: ReactNode;
  studentFilters?: number[];
  tutorFilters?: number[];
  subjectFilters?: number[];
  locationFilters?: number[];
  locationAddressFilters?: string[];
  locationCityFilters?: string[];
  locationStateFilters?: string[];
  addedStart?: Date;
  organization?: OrganizationResponseForAdmins;
  onMeetingsChange?: () => void;
  /** Changes when meetings were changed elsewhere on the page. */
  refreshKey?: number;
}) {
  // Tracks whether we are showing 7 days (week) or 1 day
  const [calendarViewType, setCalendarViewType] = useState<CalendarViewType>(
    CalendarView.WEEK
  );

  // Single anchor date for day or week view
  // (if in week mode, we display "Week of startOfWeek(selectedDate)",
  //  if in day mode, we display the exact date)
  const [selectedDate, setSelectedDate] = useState<Date>(
    moment(addedStart).startOf("week").toDate()
  );

  // For Tutoring Hours view (date range):
  const [startDate, setStartDate] = useState<Date>(
    moment().subtract(2, "week").toDate()
  );
  const [endDate, setEndDate] = useState<Date>(new Date());

  // The Communication view's dates follow the ones above once they stop
  // changing for a moment, so typing a date (each digit is a date) loads
  // once. A link to a conversation sets them at once.
  const [communicationDates, setCommunicationDates] = useState({
    startDate,
    endDate,
  });
  useEffect(() => {
    const timer = window.setTimeout(
      () =>
        setCommunicationDates((dates) =>
          dates.startDate.getTime() === startDate.getTime() &&
          dates.endDate.getTime() === endDate.getTime()
            ? dates
            : { startDate, endDate }
        ),
      COMMUNICATION_DATE_WAIT_MS
    );
    return () => window.clearTimeout(timer);
  }, [startDate, endDate]);

  // Filters for tutoring hours view
  const [meetingStatusFilter, setMeetingStatusFilter] = useState<
    MeetingStatus | undefined
  >(MeetingStatus.ACCEPTED);
  const [adminApprovalFilter, setAdminApprovalFilter] = useState<
    ApprovalStatus | undefined
  >(undefined);
  const [tutorApprovalFilter, setTutorApprovalFilter] = useState<
    ApprovalStatus | undefined
  >(undefined);

  // Filters for communication view
  const [directionFilter, setDirectionFilter] = useState<
    "all" | MessageDirection
  >(MessageDirection.INBOUND);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");

  const [meetings, setMeetings] = useState<MeetingLimitedResponse[]>([]);
  // The calendar's loaded stretch (before the filters), what it covers and
  // for whom; a refresh or another role or organization loads it again.
  const calendarStretch = useRef<{
    start: number;
    until: number;
    key: string;
    meetings: MeetingLimitedResponse[];
  }>();
  // Only the latest request's answer is shown.
  const latestRequest = useRef(0);
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();

  const { account, currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  const [currentView, setCurrentView] = useState<ViewType>(View.CALENDAR);

  // The pending-approval text's link (?view=hours) opens Tutoring Hours on
  // the tutor's sessions still waiting for their approval, from the past
  // year. Only the tutor dashboard takes it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (
      searchParams.get("view") !== "hours" ||
      currently_selected_role !== Auth0AccountRole.ORG_TUTOR
    ) {
      return;
    }
    setCurrentView(View.TUTORING_HOURS);
    setTutorApprovalFilter(ApprovalStatus.PENDING);
    setStartDate(moment().subtract(12, "months").toDate());
    setEndDate(new Date());
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
  }, [currently_selected_role, searchParams, setSearchParams]);

  // Where the Communication view puts Known / Unknown and Mark all read.
  const [communicationControls, setCommunicationControls] =
    useState<HTMLDivElement | null>(null);
  // The box the views scroll in (the Communication view loads older
  // messages as its end comes into view).
  const [viewBox, setViewBox] = useState<HTMLDivElement | null>(null);

  // A link to a conversation (?view=communication&row=&since=):
  // this view, its filters cleared so the row shows, the dates widened for
  // an older conversation, and that row opened (each time). The params are
  // then removed, so coming back never opens it again.
  const [openRequest, setOpenRequest] = useState<{
    row: string;
    at: number;
  } | null>(null);
  useEffect(() => {
    if (
      searchParams.get("view") !== View.COMMUNICATION ||
      (currently_selected_role !== Auth0AccountRole.ORG_ADMIN &&
        currently_selected_role !== Auth0AccountRole.TADPOLE_ADMIN)
    ) {
      return;
    }
    setCurrentView(View.COMMUNICATION);
    const row = searchParams.get("row");
    const since = searchParams.get("since");
    if (since) {
      const widen = (start: Date) =>
        moment(since).isBefore(start) ? moment(since).toDate() : start;
      setStartDate(widen);
      setCommunicationDates((dates) => ({
        ...dates,
        startDate: widen(dates.startDate),
      }));
    }
    if (row) {
      setDirectionFilter("all");
      setChannelFilter("all");
      setOpenRequest({ row, at: Date.now() });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    next.delete("row");
    next.delete("since");
    setSearchParams(next, { replace: true });
  }, [currently_selected_role, searchParams, setSearchParams]);

  // Where the Tasks view puts its due dates, dropdowns and Create Task.
  const [tasksDates, setTasksDates] = useState<HTMLDivElement | null>(null);
  const [tasksFilters, setTasksFilters] = useState<HTMLDivElement | null>(null);

  /**
   * The calendar's meetings (accepted by all tutors) from start to until:
   * every one (the calendar routes don't page), each with only what the
   * calendar and its meeting window read. That shape is a subset of the full
   * response's, so it stands in for it here.
   */
  async function fetchCalendarStretch(
    start: string,
    until: string
  ): Promise<MeetingLimitedResponse[]> {
    const query = {
      start,
      until,
      meetingAcceptedByAllTutors: MeetingStatus.ACCEPTED,
    };
    const meetings = await (() => {
      switch (currently_selected_role) {
        case Auth0AccountRole.ORG_ADMIN:
          return MeetingsService.getCalendarMeetingsByOrg({
            orgId: currently_selected_organization as number,
            ...query,
          });
        case Auth0AccountRole.ORG_TUTOR:
          return MeetingsService.getCalendarMeetingsForTutorsForAccount(query);
        case Auth0AccountRole.ME:
          return MeetingsService.getCalendarMeetingsForStudentsForAccount(
            query
          );
        case Auth0AccountRole.PARENT:
          return MeetingsService.getCalendarMeetingsForParentsForAccount(query);
        default:
          return Promise.resolve([]);
      }
    })();
    return meetings as unknown as MeetingLimitedResponse[];
  }

  /**
   * Shows the meetings for the current view. The calendar shows the week or
   * day from its loaded stretch, loading a new stretch only when the shown
   * one falls outside it (or when forced, after a change).
   */
  async function fetchMeetings(force = false) {
    const request = ++latestRequest.current;
    // A forced reload drops the stretch at once, so a look that overtakes it
    // loads afresh too.
    if (force) calendarStretch.current = undefined;

    try {
      let fetchedMeetings: MeetingLimitedResponse[] = [];

      if (currentView === View.CALENDAR) {
        const unit = calendarViewType === CalendarView.WEEK ? "week" : "day";
        const shownStart = moment(selectedDate).startOf(unit);
        const shownUntil = shownStart.clone().add(1, unit);
        const key = `${currently_selected_role}:${currently_selected_organization}:${refreshKey}`;
        let stretch = calendarStretch.current;
        if (
          force ||
          !stretch ||
          stretch.key !== key ||
          shownStart.valueOf() < stretch.start ||
          shownUntil.valueOf() > stretch.until
        ) {
          setStatus(PageStatus.LOADING);
          const week = moment(selectedDate).startOf("week");
          const start = week.clone().subtract(CALENDAR_PRELOAD_WEEKS, "weeks");
          const until = week.clone().add(CALENDAR_PRELOAD_WEEKS + 1, "weeks");
          const loaded = await fetchCalendarStretch(
            start.toISOString(),
            until.toISOString()
          );
          if (request !== latestRequest.current) return;
          stretch = {
            start: start.valueOf(),
            until: until.valueOf(),
            key,
            meetings: loaded,
          };
          calendarStretch.current = stretch;
        }
        fetchedMeetings = stretch.meetings.filter((meeting) => {
          const start = moment(meeting.start);
          return start.isSameOrAfter(shownStart) && start.isBefore(shownUntil);
        });
      } else if (currentView === View.TUTORING_HOURS) {
        // Meetings can change here: the calendar loads afresh after.
        calendarStretch.current = undefined;
        setStatus(PageStatus.LOADING);
        // TUTORING HOURS VIEW:
        const start = moment(startDate).startOf("day").toISOString();
        const until = moment(endDate).endOf("day").toISOString();

        // If date is invalid or start > end, handle that
        if (moment(startDate).isAfter(moment(endDate))) {
          setError({ error: "Start date cannot be after end date." });
          setStatus(PageStatus.ERROR);
          return;
        }

        // No future date beyond today (unless admin):
        if (
          currently_selected_role !== Auth0AccountRole.ORG_ADMIN &&
          moment(endDate).isAfter(moment())
        ) {
          setEndDate(new Date());
        }

        console.log("adminApprovalFilter", adminApprovalFilter);
        console.log("meetingStatusFilter", meetingStatusFilter);
        console.log("tutorApprovalFilter", tutorApprovalFilter);

        if (currently_selected_role === Auth0AccountRole.ORG_ADMIN) {
          fetchedMeetings = await MeetingsService.getMeetingsByOrg({
            orgId: currently_selected_organization as number,
            start,
            until,
            reviewedAndApprovedByAdmin: adminApprovalFilter,
            meetingAcceptedByAllTutors: meetingStatusFilter,
            reviewedAndApprovedByAllTutors: tutorApprovalFilter,
          });
        } else if (currently_selected_role === Auth0AccountRole.ORG_TUTOR) {
          fetchedMeetings =
            await MeetingsService.getMeetingsForAllTutorsForAccount({
              start,
              until,
              reviewedAndApprovedByAdmin: adminApprovalFilter,
              meetingAcceptedByThisTutor: meetingStatusFilter,
              meetingReviewedAndApprovedByThisTutor: tutorApprovalFilter,
            });
        }
        // ME and PARENT do not have access to tutoring hours.
        if (request !== latestRequest.current) return;
      }

      // Filter by students/tutors/subjects if present
      const filteredMeetings = fetchedMeetings.filter((meeting) => {
        const subjectMatch =
          !subjectFilters?.length ||
          subjectFilters.includes(meeting.org_subject.id);

        const tutorMatch =
          !tutorFilters?.length ||
          meeting.tutors.some((t) => tutorFilters.includes(t.tutor.id));

        const studentMatch =
          !studentFilters?.length ||
          meeting.students.some((s) => studentFilters.includes(s.student.id));

        const locationMatch =
          !locationFilters?.length ||
          (meeting.location?.id &&
            locationFilters.includes(meeting.location.id));

        const locationAddressMatch =
          !locationAddressFilters?.length ||
          (meeting.location?.address_line_1 &&
            locationAddressFilters.includes(meeting.location.address_line_1));

        const locationCityMatch =
          !locationCityFilters?.length ||
          (meeting.location?.city &&
            locationCityFilters.includes(meeting.location.city));

        const locationStateMatch =
          !locationStateFilters?.length ||
          (meeting.location?.state &&
            locationStateFilters.includes(meeting.location.state));

        return (
          subjectMatch &&
          tutorMatch &&
          studentMatch &&
          locationMatch &&
          locationAddressMatch &&
          locationCityMatch &&
          locationStateMatch
        );
      });

      setMeetings(filteredMeetings);
      setStatus(PageStatus.SUCCESS);
    } catch (e: any) {
      if (request !== latestRequest.current) return;
      const err = e as ApiError;
      console.error(`Error (#${err.status}): ${err.message}`);
      setStatus(PageStatus.ERROR);
      setError({ error: "An unexpected error occurred", message: err.message });
    }
  }

  // Someone booked, changed or imported meetings: tell the page, then refetch.
  function meetingsChanged() {
    onMeetingsChange?.();
    return fetchMeetings(true);
  }

  useEffect(() => {
    if (account && currently_selected_role) {
      fetchMeetings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    account,
    studentFilters,
    tutorFilters,
    subjectFilters,
    currently_selected_role,
    calendarViewType,
    startDate,
    endDate,
    meetingStatusFilter,
    adminApprovalFilter,
    tutorApprovalFilter,
    refreshKey,
  ]);

  // If addedStart changes from above (a meeting booked or accepted there),
  // load the calendar afresh and show that week.
  useEffect(() => {
    if (addedStart) {
      calendarStretch.current = undefined;
      setSelectedDate(moment(addedStart).startOf("week").toDate());
    }
  }, [addedStart]);

  // Meetings changed somewhere that doesn't reload the calendar: its next
  // look loads afresh (no reload now, which would close an open meeting).
  useMeetingsChanged(() => {
    calendarStretch.current = undefined;
  });

  // Responsive behavior for smaller screens: automatically show "Day" view if width < 1024
  let onWindowResize = () => {
    if (window.innerWidth < 1024) {
      setCalendarViewType(CalendarView.DAY);
    } else {
      setCalendarViewType(CalendarView.WEEK);
    }
  };

  useEffect(() => {
    onWindowResize();
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  // Disable tutoring hours and communication for ME/PARENT
  const tutoringHoursDisabled =
    currently_selected_role === Auth0AccountRole.ME ||
    currently_selected_role === Auth0AccountRole.PARENT;

  const communicationDisabled =
    currently_selected_role === Auth0AccountRole.ME ||
    currently_selected_role === Auth0AccountRole.PARENT ||
    currently_selected_role === Auth0AccountRole.ORG_TUTOR;

  const tasksDisabled = currently_selected_role !== Auth0AccountRole.ORG_ADMIN;
  // This admin's unread conversations (texts and emails).
  const unreadMessages = conversationCount(useUnread().summary);
  const openTasks = useOpenTaskCount(
    currently_selected_organization ?? undefined,
    !tasksDisabled
  );

  // Helper: move backward one unit (day/week) depending on view
  const goPrevious = () => {
    setSelectedDate(
      moment(selectedDate)
        .subtract(1, calendarViewType === CalendarView.DAY ? "day" : "week")
        .toDate()
    );
  };

  // Helper: move forward one unit (day/week) depending on view
  const goNext = () => {
    setSelectedDate(
      moment(selectedDate)
        .add(1, calendarViewType === CalendarView.DAY ? "day" : "week")
        .toDate()
    );
  };

  // Handle toggling to Week view
  const toggleToWeekView = () => {
    // If we're currently in day mode, jump to the Sunday for that selected day
    if (calendarViewType === CalendarView.DAY) {
      setSelectedDate(moment(selectedDate).startOf("week").toDate());
    }
    setCalendarViewType(CalendarView.WEEK);
  };

  // Handle toggling to Day view
  const toggleToDayView = () => {
    // If we're currently in week mode, pick "today's weekday"
    // offset within the *displayed* week. E.g. if today is Wednesday,
    // that is dayOfWeek=3. So we add 3 days to the Sunday of the displayed week.
    if (calendarViewType === CalendarView.WEEK) {
      const dayOfWeek = moment().day(); // 0=Sunday, 1=Monday, ...
      const newDate = moment(selectedDate)
        .startOf("week")
        .add(dayOfWeek, "days");
      setSelectedDate(newDate.toDate());
    }
    setCalendarViewType(CalendarView.DAY);
  };

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <div className="grid grid-cols-8 pb-4 border-b-2">
        <div className="col-span-8 md:col-span-5">{children}</div>
        <div className="flex flex-row items-center gap-2 col-span-8 xl:col-span-3 justify-end mt-4 md:mt-0">
          {/* If we're in calendar view, show date/week toggles */}
          {currentView === View.CALENDAR && (
            <>
              <Button
                size={ButtonSize.SMALL}
                fill={ButtonFill.HOLLOW}
                color={ButtonColor.GREEN}
                onClick={
                  calendarViewType === CalendarView.DAY
                    ? toggleToWeekView
                    : toggleToDayView
                }
                extraClasses="ml-2 px-3 py-1 border rounded-md bg-green-50"
              >
                {" "}
                {calendarViewType === CalendarView.WEEK ? "W" : "D"}{" "}
              </Button>
              {calendarViewType === CalendarView.DAY ? (
                <span className="font-bold ml-2">
                  {moment(selectedDate).format("ddd, MMMM Do")}
                </span>
              ) : (
                <span className="font-bold ml-2">
                  Week of{" "}
                  {moment(selectedDate).startOf("week").format("MMMM Do")}
                </span>
              )}

              {/* Left arrow */}
              <Button
                size={ButtonSize.SMALL}
                fill={ButtonFill.NO_BORDER}
                onClick={goPrevious}
                extraClasses="p-0"
                aria-label={
                  calendarViewType === CalendarView.DAY
                    ? "Previous day"
                    : "Previous week"
                }
              >
                <ChevronLeftIcon width="20" height="20" />
              </Button>

              {/* Right arrow */}
              <Button
                size={ButtonSize.SMALL}
                fill={ButtonFill.NO_BORDER}
                onClick={goNext}
                extraClasses="p-0"
                aria-label={
                  calendarViewType === CalendarView.DAY
                    ? "Next day"
                    : "Next week"
                }
              >
                <ChevronRightIcon width="20" height="20" />
              </Button>
            </>
          )}

          {currentView === View.TASKS && !tasksDisabled && (
            <div className="ml-2" ref={setTasksDates} />
          )}

          {[View.TUTORING_HOURS, View.COMMUNICATION].includes(currentView) && (
            <div className="ml-2">
              <DateRangeSelector
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                allowFutureDates={
                  currently_selected_role === Auth0AccountRole.ORG_ADMIN
                }
              />
            </div>
          )}

          {/* Calendar view button */}
          <Button
            size={ButtonSize.SMALL}
            fill={
              currentView === View.CALENDAR
                ? ButtonFill.HOLLOW
                : ButtonFill.NO_BORDER
            }
            color={
              currentView === View.CALENDAR
                ? ButtonColor.GREEN
                : ButtonColor.BLACK
            }
            onClick={() => {
              setCurrentView(View.CALENDAR);
              toggleToWeekView();
            }}
            extraClasses={`ml-4 border-2 p-1 !text-black`}
          >
            <CalendarIcon width="25" height="25" />
          </Button>

          {!tutoringHoursDisabled && (
            <Button
              size={ButtonSize.SMALL}
              fill={
                currentView === View.TUTORING_HOURS
                  ? ButtonFill.HOLLOW
                  : ButtonFill.NO_BORDER
              }
              color={
                currentView === View.TUTORING_HOURS
                  ? ButtonColor.GREEN
                  : ButtonColor.BLACK
              }
              disabled={tutoringHoursDisabled}
              onClick={() => {
                setCurrentView(View.TUTORING_HOURS);
                setCalendarViewType(CalendarView.DAY);
              }}
              extraClasses={`ml-2 border-2 p-1 !text-black`}
            >
              <FileTextIcon width="25" height="25" />
            </Button>
          )}

          {!communicationDisabled && (
            <Button
              size={ButtonSize.SMALL}
              fill={
                currentView === View.COMMUNICATION
                  ? ButtonFill.HOLLOW
                  : ButtonFill.NO_BORDER
              }
              color={
                currentView === View.COMMUNICATION
                  ? ButtonColor.GREEN
                  : ButtonColor.BLACK
              }
              disabled={communicationDisabled}
              onClick={() => setCurrentView(View.COMMUNICATION)}
              title="Communication"
              aria-label={
                unreadMessages > 0
                  ? `Communication, ${unreadMessages} unread`
                  : "Communication"
              }
              extraClasses={`relative ml-2 border-2 p-1 !text-black`}
            >
              <EnvelopeClosedIcon width="25" height="25" />
              <CountBadge count={unreadMessages} color="bg-blue-500" />
            </Button>
          )}

          {!tasksDisabled && (
            <Button
              size={ButtonSize.SMALL}
              fill={
                currentView === View.TASKS
                  ? ButtonFill.HOLLOW
                  : ButtonFill.NO_BORDER
              }
              color={
                currentView === View.TASKS
                  ? ButtonColor.GREEN
                  : ButtonColor.BLACK
              }
              onClick={() => setCurrentView(View.TASKS)}
              title="Tasks"
              aria-label={
                openTasks > 0 ? `Tasks, ${openTasks} still to do` : "Tasks"
              }
              extraClasses={`relative ml-2 border-2 p-1 !text-black`}
            >
              <GoChecklist size={25} />
              <CountBadge count={openTasks} color="bg-[color:var(--green)]" />
            </Button>
          )}
        </div>
      </div>

      {currentView === View.TUTORING_HOURS && !tutoringHoursDisabled && (
        <div className="mt-2">
          <MeetingFilters
            meetingStatusFilter={meetingStatusFilter}
            setMeetingStatusFilter={setMeetingStatusFilter}
            adminApprovalFilter={adminApprovalFilter}
            setAdminApprovalFilter={setAdminApprovalFilter}
            tutorApprovalFilter={tutorApprovalFilter}
            setTutorApprovalFilter={setTutorApprovalFilter}
          />
        </div>
      )}

      {currentView === View.COMMUNICATION && !communicationDisabled && (
        <div className="mt-2 flex flex-col md:flex-row md:items-center gap-4">
          <CommunicationFilters
            directionFilter={directionFilter}
            setDirectionFilter={setDirectionFilter}
            channelFilter={channelFilter}
            setChannelFilter={setChannelFilter}
          />
          <div ref={setCommunicationControls} />
        </div>
      )}

      {currentView === View.TASKS && !tasksDisabled && (
        <div className="mt-2" ref={setTasksFilters} />
      )}

      {status !== PageStatus.ERROR ? (
        <>
          <div
            ref={setViewBox}
            className={`mt-3 max-h-[75vh] overflow-y-scroll ${
              currentView !== View.CALENDAR
                ? "border-2 border-gray-300 rounded-lg"
                : ""
            }`}
          >
            {currentView === View.CALENDAR && currently_selected_role ? (
              <CalendarWrapper
                role={currently_selected_role}
                weekStart={moment(selectedDate)
                  .startOf(
                    calendarViewType === CalendarView.WEEK ? "week" : "day"
                  )
                  .toDate()}
                events={meetings}
                setEvents={meetingsChanged}
                days={calendarViewType === CalendarView.DAY ? 1 : 7}
                status={status}
                calendarViewType={calendarViewType}
              />
            ) : currentView === View.TUTORING_HOURS &&
              !tutoringHoursDisabled ? (
              <TutoringHours
                startingMeetings={meetings.filter((m) =>
                  moment(m.start).isBetween(
                    moment(startDate).startOf("day"),
                    moment(endDate).endOf("day"),
                    undefined,
                    "[]"
                  )
                )}
                status={status}
                isAdmin={currently_selected_role === Auth0AccountRole.ORG_ADMIN}
              />
            ) : currentView === View.COMMUNICATION && !communicationDisabled ? (
              <OrgCommunication
                organization={organization as OrganizationResponseForAdmins}
                startDate={communicationDates.startDate}
                endDate={communicationDates.endDate}
                directionFilter={directionFilter}
                channelFilter={channelFilter}
                controls={communicationControls}
                openRequest={openRequest}
                scrollRoot={viewBox}
              />
            ) : currentView === View.TASKS && !tasksDisabled ? (
              <ManageTasksDialog
                embedded
                startingView="all_tasks"
                startingStatus={TaskStatus.PENDING}
                slots={{
                  dates: tasksDates,
                  filters: tasksFilters,
                }}
              />
            ) : null}
          </div>
          <div className="w-full text-right mt-3 flex justify-end gap-2">
            {currentView === View.TASKS && !tasksDisabled
              ? null
              : currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
                  <>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          color={ButtonColor.GREEN}
                          size={ButtonSize.SMALL}
                          title="Import Meetings"
                        >
                          <CgImport size={15} />
                        </Button>
                      </DialogTrigger>

                      <ImportMeetingsDialog
                        organizationId={
                          currently_selected_organization as number
                        }
                        organization={organization}
                        onSuccess={meetingsChanged}
                      />
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button color={ButtonColor.GREEN}>Export</Button>
                      </DialogTrigger>

                      <ExportDialog
                        organizationId={
                          currently_selected_organization as number
                        }
                      />
                    </Dialog>
                  </>
                )}
          </div>
        </>
      ) : (
        <ErrorBlock status={status} title="Oh no!" message={error?.message} />
      )}
    </div>
  );
}

export default UpcomingMeetings;
